import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { DeviceMobile, AndroidLogo, AppleLogo, Globe, DownloadSimple } from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { NfcWriteDialog } from "@/components/NfcWriteDialog";
import { useApp } from "@/context/AppContext";

const APP_INSTALL_URL = "https://safe-buddy.com/app";

export function WriteTagButton({ tag, className = "", label, style, iconColor }) {
  const { t } = useApp();
  const [fallbackOpen, setFallbackOpen] = useState(false);
  const [nfcOpen, setNfcOpen] = useState(false);

  const buddyUrl = `${window.location.origin}/t/${tag.code}`;
  const name = tag.label || t("unnamed");

  const launch = () => {
    const deep = `safebuddy://write?url=${encodeURIComponent(buddyUrl)}&name=${encodeURIComponent(name)}`;
    let switched = false;
    const onHide = () => { switched = true; };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("blur", onHide);
    try { window.location.href = deep; } catch { /* scheme not registered */ }
    setTimeout(() => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("blur", onHide);
      if (!switched && !document.hidden) setFallbackOpen(true);
    }, 1200);
  };

  const btnClass = className ||
    "sb-btn w-full py-2.5 mt-2 text-white font-medium flex items-center justify-center gap-2 text-sm";

  return (
    <>
      <button data-testid={`write-tag-${tag.code}`} onClick={launch} className={btnClass}
        style={style || { background: "var(--app-primary)" }}>
        <DeviceMobile size={18} weight="bold" color={iconColor} />{label || t("writeTag")}
      </button>

      <Dialog open={fallbackOpen} onOpenChange={setFallbackOpen}>
        <DialogContent data-testid="write-tag-fallback" className="max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <DeviceMobile size={22} weight="duotone" />{t("writeTagTitle")}
            </DialogTitle>
            <DialogDescription className="mt-1.5">{t("writeTagFallbackDesc")}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-2xl border-2 border-gray-800 bg-white p-4" data-testid="write-tag-install">
              <div className="flex items-center gap-2 mb-1">
                <DownloadSimple size={20} weight="bold" style={{ color: "var(--app-strong)" }} />
                <p className="font-heading font-semibold text-gray-900">{t("writeTagInstallTitle")}</p>
              </div>
              <p className="text-sm font-body text-gray-600 mb-3">{t("writeTagInstallDesc")}</p>
              <div className="flex flex-col items-center gap-2">
                <div className="bg-white p-2 rounded-xl border-2 border-gray-800">
                  <QRCodeSVG data-testid="write-tag-qr" value={APP_INSTALL_URL} size={124} />
                </div>
                <span className="text-xs font-body text-gray-500">{t("writeTagScan")}</span>
              </div>
              <div className="flex gap-2 mt-3">
                <a data-testid="write-tag-playstore" href={APP_INSTALL_URL} target="_blank" rel="noreferrer"
                  className="sb-btn flex-1 py-2.5 bg-white font-medium flex items-center justify-center gap-2 text-sm">
                  <AndroidLogo size={18} weight="fill" style={{ color: "#3DDC84" }} />{t("playStoreBtn")}
                </a>
                <a data-testid="write-tag-appstore" href={APP_INSTALL_URL} target="_blank" rel="noreferrer"
                  className="sb-btn flex-1 py-2.5 bg-white font-medium flex items-center justify-center gap-2 text-sm">
                  <AppleLogo size={18} weight="fill" className="text-gray-900" />{t("appStoreBtn")}
                </a>
              </div>
            </div>

            <div>
              <button data-testid="write-tag-browser" onClick={() => { setFallbackOpen(false); setNfcOpen(true); }}
                className="sb-btn w-full py-3 text-white font-medium flex items-center justify-center gap-2"
                style={{ background: "var(--app-primary)" }}>
                <Globe size={18} weight="bold" />{t("writeTagBrowser")}
              </button>
              <p className="text-xs font-body text-gray-500 text-center mt-2">{t("writeTagBrowserHint")}</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <NfcWriteDialog open={nfcOpen} onOpenChange={setNfcOpen} presetTag={tag} />
    </>
  );
}
